<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-12">
          <h6 class="h2 text-white d-inline-block mb-0">Crear Recibo</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb />
          </nav>
        </div>
      </div>
    </base-header>

    <div v-if="order" class="container-fluid mt--6">
      <div class="row justify-content-center">
        <div class="col-12 card-wrapper">
          <div class="card">
            <div class="card-header">
              <div class="card-body">
                <order-info :receipt="receipt" :order="order" :edit="true" @get-quotation="setDollarQuotation" />

                <div v-if="canLoad">
                  <hr />

                  <concept-payment-form
                    ref="conceptPaymentForm"
                    class="mb-4"
                    :receipt="receipt"
                    :updated-debt-in-dollars="debtInDollars"
                    :updated-debt-in-pesos="debtInPesos"
                    @addItem="addItem"
                  />

                  <concept-payment-list
                    class="mb-4"
                    :items="receipt.conceptPayments"
                    :edit="true"
                    @deleteItem="deleteItem"
                  />

                  <payment-detail v-if="receipt.orderId" class="mb-4" :order-id="receipt.orderId" />

                  <payment-by-months v-if="order" class="mb-4" :order="order" :receipt="receipt" />

                  <debt-info
                    class="mb-4"
                    :order-dollar-quote="order.dollarQuote"
                    :current-dollar-quote="currentDollarQuotation"
                    :debt-in-dollars="debtInDollars"
                    :debt-in-pesos="debtInOrderPesos"
                  />
                </div>
              </div>

              <div v-if="canLoad" class="class-footer">
                <div class="text-right">
                  <span v-if="debtInPesos < 0" class="text-danger d-block">Monto a cancelar excedido</span>
                  <base-button v-if="canSave" type="primary" :disabled="!hasItems || debtInDollars < 0" @click="onSave">
                    Guardar
                  </base-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import swal from 'sweetalert2';
import { mapState, mapActions, mapGetters } from 'vuex';

import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import { subtract, divide } from '@/util/math';

import OrderInfo from './List/OrderInfo.vue';
import DebtInfo from './List/DebtInfo.vue';
import PaymentByMonths from './List/PaymentByMonths.vue';
import PaymentDetail from './List/PaymentDetail.vue';
import ConceptPaymentForm from './Forms/ConceptPaymentForm.vue';
import ConceptPaymentList from './List/ConceptPaymentList.vue';
import OrdersService from '@/services/resources/orders.service';

export default {
  name: 'ReceiptCreate',
  components: {
    RouteBreadCrumb,
    OrderInfo,
    DebtInfo,
    PaymentByMonths,
    ConceptPaymentForm,
    ConceptPaymentList,
    PaymentDetail,
  },

  data() {
    return {
      order: null,
      debtInDollars: 0,
      debtInPesos: 0,
      debtInOrderPesos: 0,
      receipt: {
        id: null,
        orderId: null,
        createdAt: moment().format('YYYY-MM-DD'),
        dollarQuotation: 0,
        conceptPayments: [],
      },
    };
  },

  computed: {
    ...mapGetters('dollarQuotation', ['currentDollarQuotation']),

    ...mapState('models/order', { orderDebt: 'debt' }),

    hasItems() {
      return Boolean(this.receipt.conceptPayments.length > 0);
    },

    canLoad() {
      return Boolean(this.orderDebt && this.receipt.dollarQuotation !== 0);
    },

    canSave() {
      return this.order.state.name === OrdersService._STATE_ACCEPTED;
    },
  },

  created() {
    this.receipt.orderId = this.$route.params.orderId;
    this.fetchOrder();
  },

  methods: {
    ...mapActions('models/order', ['fetchOrderDebt']),

    ...mapState('models/order', { orderDebt: 'debt' }),

    updateDebt() {
      this.debtInDollars = this.getDebt(this.orderDebt.dollar, 'amountInDollars');
      this.debtInPesos = this.getDebt(this.orderDebt.pesos, 'amountInPesos');
      this.debtInOrderPesos = this.getDebt(this.orderDebt.pesosOrder, 'amountInPesos');
    },

    getDebt(total, prop) {
      const canceled = [...this.receipt.conceptPayments].reduce(
        (canceled, conceptPayment) => Number(canceled) + Number(conceptPayment[prop]),
        0
      );

      return subtract(total, canceled);
    },

    addItem(item) {
      this.receipt.conceptPayments.push(item);
      this.updateDebt();
      this.$refs.conceptPaymentForm.clean();
    },

    deleteItem(_, index) {
      this.receipt.conceptPayments.splice(index, 1);
      this.updateDebt();
    },

    async setDollarQuotation(dollarQuotation) {
      this.receipt.dollarQuotation = Number(dollarQuotation);
      await this.fetchOrderDebt({
        orderId: this.receipt.orderId,
        dollarQuotation: this.receipt.dollarQuotation,
      });
      this.debtInDollars = this.orderDebt.dollar;
      this.debtInPesos = this.orderDebt.pesos;
      this.debtInOrderPesos = this.orderDebt.pesosOrder;

      this.addAmountInDollarToConceptPayments();
    },

    addAmountInDollarToConceptPayments() {
      if (this.receipt.conceptPayments.length > 0) {
        this.receipt.conceptPayments = this.receipt.conceptPayments.map((concept) => {
          const amountInDollars = divide(concept.amountInPesos, this.receipt.dollarQuotation);

          return {
            ...concept,
            amountInDollars,
          };
        });
      }
    },

    async onSave() {
      this.$services
        .resource('receipts')
        .create(this.receipt)
        .then(() => {
          swal({
            title: 'Listo!',
            text: 'El Recibo fue creado con éxito',
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-success',
            type: 'success',
          }).then(() => {
            this.$router.push({ name: 'ReceiptAcceptedOrders' });
          });
        })
        .catch((error) => this.$error(error.response.data.message));
    },

    fetchOrder() {
      const params = {
        include: ['producer.distributor.seller', 'state'],
        append: ['total', 'total-in-pesos'],
      };

      this.$services
        .resource('orders')
        .show(this.receipt.orderId, params)
        .then((res) => res.data.data)
        .then((order) => (this.order = order));
    },
  },
};
</script>
